import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utilities/axios";
import { getCategories, getSynthesis } from "./categories";
import { getPlancompta } from "./plancompta";
import { getElements, getElementsWithSum } from "./elements";

// API calls

export const linkElements = createAsyncThunk(
  "linking/linkElements",
  async (values, thunkAPI) => {
    let data;
    try {
      const response = await axios.post("/api/elements/link", {
        elementId1: values.elementId1,
        elementId2: values.elementId2,
        dateTreso: values.dateTreso,
        status: values.statutTreso,
        // ...values.linkData,
      });
      data = await response.data;
      if (response.status === 200) {
        // Refresh data after successful linking
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
        } else {
          thunkAPI.dispatch(getCategories(values?.project)).then(() => {
            thunkAPI.dispatch(getSynthesis(values?.synthesis));
          });
        }

        return data?.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const unlinkAllElements = createAsyncThunk(
  "linking/unlinkAllElements",
  async (values, thunkAPI) => {
    let data;
    try {
      const response = await axios.delete(
        `/api/elements/${values.elementId}/unlink-all`
      );
      data = await response.data;
      if (response.status === 200) {
        // Refresh data after successful unlinking
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
        } else {
          thunkAPI.dispatch(getCategories(values?.project)).then(() => {
            thunkAPI.dispatch(getSynthesis(values?.synthesis));
          });
        }

        thunkAPI.dispatch(getElementsWithSum(values?.sum));
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error({ err });
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const unlinkElement = createAsyncThunk(
  "linking/unlinkElement",
  async (values, thunkAPI) => {
    let data;
    try {
      const response = await axios.delete(
        `/api/elements/${values.elementId}/unlink`
      );
      data = await response.data;
      if (response.status === 200) {
        // Refresh data after successful unlinking
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
        }

        if (values.isPlancompta) {
          thunkAPI.dispatch(getPlancompta(values?.project)).then(() => {
            thunkAPI.dispatch(getSynthesis(values?.synthesis));
          });
        } else {
          thunkAPI.dispatch(getCategories(values?.project)).then(() => {
            thunkAPI.dispatch(getSynthesis(values?.synthesis));
          });
        }

        thunkAPI.dispatch(getElementsWithSum(values?.sum));
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error({ err });
      return thunkAPI.rejectWithValue(err);
    }
  }
);

const initialState = {
  linkingInfo: {
    thedate: null,
    theaccount: null,
    status: null,
    type: null,
    relatedToId: null,
    relatedToElement: null,
  },

  islinkingModeActivated: false,
  isShowingLink: false,
  isUnlinking: false,
  linkStatus: "idle",
  unlinkStatus: "idle",
  unlinkAllStatus: "idle",
  linkError: null,
  unlinkError: null,
  unlinkAllError: null,
  showLinkingGroupInTable: {
    isShowing: false,
    element: null,
    groupId: null,
  },
};

const linkingSlice = createSlice({
  name: "linking",
  initialState,
  reducers: {
    setLinking: (state, action) => {
      state.linking = action.payload;
    },
    setShowLinkingGroupInTable: (state, action) => {
      state.showLinkingGroupInTable = action.payload;
    },
    setIsLinkingModeActivated: (state, action) => {
      state.islinkingModeActivated = action.payload;
    },
    setIsShowingLink: (state, action) => {
      state.isShowingLink = action.payload;
    },
    setIsUnlinking: (state, action) => {
      state.isUnlinking = action.payload;
    },
    setLinkingInfo: (state, action) => {
      state.linkingInfo = action.payload;
    },
    clearErrors: (state) => {
      state.linkError = null;
      state.unlinkError = null;
      state.unlinkAllError = null;
    },
    clearShowLinkingGroupInTable: (state) => {
      state.showLinkingGroupInTable = {
        isShowing: false,
        element: null,
        groupId: null,
      };
    },
  },
  extraReducers: {
    [linkElements.pending]: (state, action) => {
      state.linkStatus = "loading";
      state.linkError = null;
    },
    [linkElements.fulfilled]: (state, action) => {
      state.linkStatus = "succeeded";
      state.linkError = null;
    },
    [linkElements.rejected]: (state, action) => {
      state.linkStatus = "failed";
      state.linkError = action.payload;
    },
    [unlinkAllElements.pending]: (state, action) => {
      state.unlinkAllStatus = "loading";
      state.unlinkAllError = null;
    },
    [unlinkAllElements.fulfilled]: (state, action) => {
      state.unlinkAllStatus = "succeeded";
      state.unlinkAllError = null;
    },
    [unlinkAllElements.rejected]: (state, action) => {
      state.unlinkAllStatus = "failed";
      state.unlinkAllError = action.payload;
    },
    [unlinkElement.pending]: (state, action) => {
      state.unlinkStatus = "loading";
      state.unlinkError = null;
    },
    [unlinkElement.fulfilled]: (state, action) => {
      state.unlinkStatus = "succeeded";
      state.unlinkError = null;
    },
    [unlinkElement.rejected]: (state, action) => {
      state.unlinkStatus = "failed";
      state.unlinkError = action.payload;
    },
  },
});

export const {
  setLinking,
  setIsLinkingModeActivated,
  setIsShowingLink,
  setIsUnlinking,
  setLinkingInfo,
  clearErrors,
  setShowLinkingGroupInTable,
  clearShowLinkingGroupInTable,
} = linkingSlice.actions;

export const reducer = linkingSlice.reducer;

export default linkingSlice;
